import { ReactNode } from 'react';
import {
  Cell,
  CellEditBehavior,
  Region,
  Slot,
  SlotRenderContentProps,
  SlotRenderContentResult,
} from '../types';

export class CustomSlot<RawData = undefined> implements Slot<RawData> {
  public readonly disableContextMenu: boolean;
  public readonly region: Region;
  public readonly disabled: boolean;
  readonly #renderContent?: (
    editing: boolean,
    onBlur: () => void,
    refocusSheet: (x: number, y: number) => void,
    disabled?: boolean,
    readOnly?: boolean,
  ) => ReactNode;
  readonly label?: string;

  constructor(
    anchor: Cell,
    width: number,
    height: number,
    disabled: boolean,
    renderContent?: (
      editing: boolean,
      onBlur: (skipCommit?: boolean) => void,
      refocusSheet: (x: number, y: number) => void,
      disabled?: boolean,
      readOnly?: boolean,
    ) => ReactNode,
    private readonly options?: {
      editBehavior?: CellEditBehavior;
      className?: string;
      backgroundClassName?: string;
      selectedBorderStyle?: string;
      data?: readonly RawData[];
      style?: React.CSSProperties;
      overrideGridStyles?: boolean;
      disableContextMenu?: boolean;
    },
    label?: string,
  ) {
    this.disableContextMenu = options?.disableContextMenu ?? false;
    this.disabled = disabled;
    this.region = {
      anchor,
      height,
      width,
      isMerged: true,
    };
    this.#renderContent = renderContent;
    this.label = label;
  }

  public renderContent({
    cell,
    onBlur,
    editing,
    refocusSheet,
    readOnly,
  }: SlotRenderContentProps): SlotRenderContentResult {
    let finalStyle = this.options?.style;

    // If using grid override, merge in the dynamic row positioning
    if (this.options?.overrideGridStyles && this.options?.style) {
      finalStyle = {
        ...this.options.style,
        gridRowStart: cell.y + 1,
        gridRowEnd: `span ${this.region.height}`,
      };
    }

    return {
      content: this.#renderContent?.(editing, onBlur, refocusSheet, this.disabled, readOnly),
      className: this.options?.className,
      selectedBorderStyle: this.options?.selectedBorderStyle,
      backgroundClassName: this.options?.backgroundClassName,
      style: finalStyle,
      overrideGridStyles: this.options?.overrideGridStyles,
    };
  }

  public getEditBehavior(): CellEditBehavior {
    if (this.disabled) return { type: 'skip' };
    return this.options?.editBehavior ?? { type: 'skip' };
  }

  public getRawData() {
    return this.options?.data;
  }
}
